import axios, { axiosV2, WAREHOUSE_AUTH_V2 } from "../../../utils/axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// @mui
import { alpha } from "@mui/material/styles";
import { Box, Divider, Typography, MenuItem } from "@mui/material";
// routes
import { PATH_AUTH } from "../../../routes/paths";
// auth
import { useAuthContext } from "../../../auth/useAuthContext";
// components
import { CustomAvatar } from "../../../components/custom-avatar";
import { useSnackbar } from "../../../components/snackbar";
import MenuPopover from "../../../components/menu-popover";
import { IconButtonAnimate } from "../../../components/animate";
import { gatherBrowserInfo, getAuthBrowserInfo } from "../../../utils/gatherBrowserInfo"
// sections
import WarehouseSelectionModal from "../../../sections/auth/WarehouseSelectionModal";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export default function AccountPopover() {
  const navigate = useNavigate();

  const { user, logout, warehouses } = useAuthContext();

  const { enqueueSnackbar } = useSnackbar();

  const [openPopover, setOpenPopover] = useState(null);

  const [browserInfo, setBrowserInfo] = useState(null);

  const [openSwitchWarehouse, setOpenSwitchWarehouse] = useState(false);

  const canSwitchWarehouse = !user?.isImpersonating && warehouses?.length > 1;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const browserInformation = await gatherBrowserInfo();
        setBrowserInfo(browserInformation);
      } catch (error) {
        console.error('Error fetching info:', error);
      }
    };

    fetchData();
  }, []);

  const handleOpenPopover = (event) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleOpenSwitchWarehouse = () => {
    handleClosePopover();
    setOpenSwitchWarehouse(true);
  };

  const handleLogout = async () => {
    if (user?.isImpersonating) {
      const adminAppUrl = process.env.REACT_APP_ADMIN_APP_URL;
      try {
        await axios.post(`${backendUrl}/authw/impersonation/end`);
      } catch (error) {
        // An expired/revoked session must not strand local credentials.
      } finally {
        logout();
        enqueueSnackbar("Impersonation session ended", { variant: "success" });
        handleClosePopover();
        if (adminAppUrl) {
          window.location.assign(
            new URL(user?.adminReturnPath || "/dashboard/franchise/list", adminAppUrl).toString()
          );
        } else {
          navigate(PATH_AUTH.login, { replace: true });
        }
      }
      return;
    }

    try {
      const refreshToken = localStorage.getItem("refreshToken");
      const resp = await axiosV2.post(`${WAREHOUSE_AUTH_V2}/logout`, {
        refreshToken,
        browserInfo: getAuthBrowserInfo(browserInfo),
      });
      if (resp.status === 204) {
        logout();
        navigate(PATH_AUTH.login, { replace: true });
        handleClosePopover();
        window.location.reload();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to logout!", { variant: "error" });
    }
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpenPopover}
        sx={{
          p: 0,
          ...(openPopover && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              position: "absolute",
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <CustomAvatar src={user?.photoURL} alt={user?.displayName} name={user?.displayName} />
      </IconButtonAnimate>

      <MenuPopover open={openPopover} onClose={handleClosePopover} sx={{ width: 200, p: 0 }}>
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.displayName}
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        {canSwitchWarehouse && (
          <>
            <MenuItem onClick={handleOpenSwitchWarehouse} sx={{ m: 1 }}>
              Switch Warehouse
            </MenuItem>

            <Divider sx={{ borderStyle: "dashed" }} />
          </>
        )}

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          {user?.isImpersonating ? "Back to Admin" : "Logout"}
        </MenuItem>
      </MenuPopover>

      {canSwitchWarehouse && (
        <WarehouseSelectionModal
          open={openSwitchWarehouse}
          mode="switch"
          onClose={() => setOpenSwitchWarehouse(false)}
        />
      )}
    </>
  );
}
